import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { EyeIcon } from '@/ui/icons';
import { api } from '@/utils/api';

type Post = {
  id: string;
  title: string;
  slug: string;
  excerpt?: string;
  coverImageUrl?: string;
  category?: { name: string; slug: string };
  publishedAt?: string;
  viewCount?: number;
};

export function HomePage() {
  const PAGE_SIZE = 12; // 4 rows * 3 columns
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const siteOrigin = typeof window !== 'undefined' ? window.location.origin : '';
  const siteUrl = siteOrigin + '/';
  const jsonLdWebsite = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Edirne Süleyman Demirel Fen Lisesi Arşiv',
    url: siteUrl,
    publisher: {
      '@type': 'Organization',
      name: 'Edirne Süleyman Demirel Fen Lisesi',
      logo: siteOrigin + '/esdfl.png'
    }
  }), [siteOrigin, siteUrl]);
  const jsonLdItemList = useMemo(() => posts.length ? ({
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: posts.map((p, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      url: siteOrigin + '/yazi/' + p.slug
    }))
  }) : null, [posts, siteOrigin]);

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const res = await api.get(`/api/public/posts?sort=recent&limit=${PAGE_SIZE}&offset=0`);
        if (!ignore) {
          setPosts(res.posts ?? []);
          setHasMore(!!res.hasMore || (res.posts?.length === PAGE_SIZE));
          setOffset((res.posts?.length ?? 0));
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => {
      ignore = true;
    };
  }, []);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const res = await api.get(`/api/public/posts?sort=recent&limit=${PAGE_SIZE}&offset=${offset}`);
      const newPosts: Post[] = res.posts ?? [];
      setPosts((p) => [...p, ...newPosts]);
      setOffset(offset + newPosts.length);
      setHasMore(!!res.hasMore || (newPosts.length === PAGE_SIZE));
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <div>
      <Helmet>
        <title>Edirne Süleyman Demirel Fen Lisesi Arşiv</title>
        <meta name="description" content="Edirne Süleyman Demirel Fen Lisesi Arşivindeki en son yazılar ve duyurular." />
        <meta property="og:title" content="Edirne Süleyman Demirel Fen Lisesi Arşiv - En Son Yazılar" />
        <meta property="og:description" content="Edirne Süleyman Demirel Fen Lisesi Arşivindeki en son yazılar ve duyurular." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href={siteUrl} />
        <script type="application/ld+json">{JSON.stringify(jsonLdWebsite)}</script>
        {jsonLdItemList && <script type="application/ld+json">{JSON.stringify(jsonLdItemList)}</script>}
      </Helmet>
      <h1 className="text-2xl md:text-3xl font-serif font-bold mb-6">En Son Yazılar</h1>
      {loading && <div>Yükleniyor…</div>}
      {!loading && posts.length === 0 && <div>Henüz içerik yok.</div>}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {posts.map((post) => (
          <Link to={`/yazi/${post.slug}`} key={post.id} className="card overflow-hidden hover:shadow-md transition">
            {post.coverImageUrl && (
              <img src={post.coverImageUrl} alt={post.title} className="h-44 w-full object-cover" />
            )}
            <div className="p-4">
              <h2 className="text-lg font-semibold mb-1">{post.title}</h2>
              {post.category && (
                <Link
                  to={`/kategori/${post.category.slug}`}
                  className="inline-flex items-center text-xs font-medium text-brand-700 bg-brand-50 px-2 py-1 rounded-full mb-2 hover:bg-brand-100 transition"
                  onClick={(e) => e.stopPropagation()}
                >
                  {post.category.name}
                </Link>
              )}
              {post.excerpt && <p className="text-sm text-gray-600 line-clamp-3">{post.excerpt}</p>}
              <div className="mt-2 text-sm text-gray-600 inline-flex items-center gap-1">
                <EyeIcon /> {post.viewCount ?? 0}
              </div>
            </div>
          </Link>
        ))}
      </div>
      {hasMore && (
        <div className="flex justify-center mt-6">
          <button className="btn" onClick={loadMore} disabled={loadingMore}>
            {loadingMore ? 'Yükleniyor…' : 'Daha fazla göster...'}
          </button>
        </div>
      )}
    </div>
  );
}


